import React, { useEffect, useMemo, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { useTranslation } from "react-i18next";
import { Summarize } from "@mui/icons-material";
import { Box, Typography } from "@mui/material";
import CustomDataGrid from "../../../../components/ui/customDataGrid/customDataGrid.component";
import { SETTLEMENTS_ENDPOINT } from "../../../../consts";
import { formatNumber } from "../../../../utils";
import axios from "../../../../api/axios";
import { useParams, useNavigate } from "react-router";

export const SaleSummary = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [overallTotal, setOverallTotal] = useState<number | string | null>(null);

  useEffect(() => {
    let isMounted = true;

    const fetchOverallTotal = async () => {
      try {
        const response = await axios.get(`${SETTLEMENTS_ENDPOINT}by-sale-date/`, {
          params: { contract_id: id, page_size: 1 },
        });
        if (isMounted) {
          setOverallTotal(response.data?.overall_total_value ?? null);
        }
      } catch {
        if (isMounted) {
          setOverallTotal(null);
        }
      }
    };

    fetchOverallTotal();

    window.addEventListener("settlementUpdated", fetchOverallTotal);
    window.addEventListener("settlementConfirmed", fetchOverallTotal);

    return () => {
      isMounted = false;
      window.removeEventListener("settlementUpdated", fetchOverallTotal);
      window.removeEventListener("settlementConfirmed", fetchOverallTotal);
    };
  }, [id]);

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "sale_date",
        headerName: t("Sale Date"),
        type: "date",
        valueGetter: (value) => (value ? new Date(value) : null),
      },
      {
        field: "equipment",
        headerName: t("Equipment"),
        flex: 1,
        sortable: false,
        valueGetter: (value, row) => {
          if (!row.equipment || row.equipment.length === 0) return "-";
          return row.equipment
            .map(
              (eq: any) =>
                `${eq.name} - ${formatNumber(eq.settlement_total)}`
            )
            .join(", ");
        },
      },
      {
        field: "total_value",
        headerName: t("Total Value"),
        type: "number",
        valueFormatter: (value) => formatNumber(value),
      },
    ],
    [t]
  );

  const handleRowClick = (rowId: string, row: any) => {
    // rowId is already the sale_date due to getRowId configuration
    const saleDate = rowId || row?.sale_date;
    if (saleDate) {
      navigate(`/contracts/${id}/sale-summary/${saleDate}`);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      {overallTotal !== null && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1,
            mb: 1,
            py: 1.5,
            px: 2,
            borderRadius: "var(--defaultRadius)",
            backgroundColor: "var(--dataGridFooterBg)",
            border: "1px solid var(--dataGridBorderColor)",
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t("Total income from the start")}:
          </Typography>
          <Typography variant="subtitle1" fontWeight="bold">
            {formatNumber(overallTotal)}
          </Typography>
        </Box>
      )}
      <CustomDataGrid
        icon={<Summarize />}
        title={t("Sale summary")}
        url={`${SETTLEMENTS_ENDPOINT}by-sale-date/?contract_id=${id}`}
        gridSettingsKey="CONTRACT_SALE_SUMMARY"
        columns={columns}
        onRowClick={handleRowClick}
        dataGridProps={{
          getRowId: (row) => row.sale_date,
          disableColumnFilter: true,
        }}
      />
    </Box>
  );
};
